/**
 * Helpers for surfacing the client's current roadmap step tasks on dashboards.
 */

const IN_PROGRESS_STATUSES = new Set(['in-progress', 'in_progress', 'active']);

export function getRoadmapTaskStepStorageKey(userId, itemId) {
  if (!userId || !itemId) return null;
  return `roadmap_task_step_${userId}_${itemId}`;
}

export function getSavedRoadmapTaskStep(userId, itemId, taskCount) {
  const key = getRoadmapTaskStepStorageKey(userId, itemId);
  if (!key || !taskCount) return 1;
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const step = parseInt(saved, 10);
      if (!Number.isNaN(step)) {
        return Math.max(1, Math.min(step, taskCount));
      }
    }
  } catch {
    // ignore
  }
  return 1;
}

export function findInProgressRoadmapIndex(items) {
  if (!items?.length) return -1;
  return items.findIndex((item) => IN_PROGRESS_STATUSES.has(item.status));
}

export function transformClientRoadmapTemplates(clientRoadmap) {
  if (!Array.isArray(clientRoadmap)) return [];

  return clientRoadmap
    .filter((template) => template.status !== 'hidden' && template.status !== 'Hidden')
    .map((template) => {
      let status = 'locked';
      if (template.status === 'completed' || template.status === 'done') {
        status = 'completed';
      } else if (IN_PROGRESS_STATUSES.has(template.status)) {
        status = 'in-progress';
      } else if (
        template.status === 'not-started' ||
        template.status === 'not_started' ||
        template.status === 'locked'
      ) {
        status = 'locked';
      }

      return {
        id: template.clientRoadmapId || template.id || `template-${Date.now()}-${Math.random()}`,
        title: template.title || 'Untitled',
        description: template.description || '',
        emoji: template.emoji || null,
        department: (template.department || 'general').toLowerCase(),
        status,
        tasks: template.tasks || [],
        order: template.order,
        allOrder: template.allOrder,
      };
    })
    .sort((a, b) => {
      const orderA = a.order || a.allOrder || 0;
      const orderB = b.order || b.allOrder || 0;
      return orderA - orderB;
    });
}

/**
 * Map incomplete roadmap sub-tasks from the current in-progress pillar (from current
 * stepper position onward) into dashboard task rows.
 */
/** Onboarding pillar shown on Roadmap during onboarding mode. */
export const ONBOARDING_ROADMAP_ITEMS = [
  {
    id: 'onboarding-1',
    title: 'Onboarding',
    status: 'in-progress',
    tasks: [
      {
        id: 'task-1',
        title: 'Sign your NDA',
        description: 'Review and sign the Non-Disclosure Agreement to protect sensitive information',
        completed: false,
      },
      {
        id: 'task-2',
        title: 'Complete your Intake',
        description: 'Fill out your business profile and share your current challenges and goals',
        completed: false,
      },
      {
        id: 'task-3',
        title: 'Create your Roadmap',
        description: 'Work with our team to create your personalized business roadmap',
        completed: false,
      },
    ],
  },
];

export function getCurrentRoadmapStepTasks(roadmapItems, userId, { useFirstWhenNoInProgress = false } = {}) {
  let inProgressIndex = findInProgressRoadmapIndex(roadmapItems);
  if (inProgressIndex === -1 && useFirstWhenNoInProgress && roadmapItems.length > 0) {
    inProgressIndex = 0;
  }
  if (inProgressIndex === -1) return [];

  const currentItem = roadmapItems[inProgressIndex];
  const templateTasks = currentItem?.tasks;
  if (!templateTasks?.length) return [];

  const currentTaskStep = getSavedRoadmapTaskStep(userId, currentItem.id, templateTasks.length);
  const startIndex = Math.max(0, currentTaskStep - 1);

  return templateTasks
    .map((task, taskIndex) => ({ task, taskIndex }))
    .filter(({ task, taskIndex }) => taskIndex >= startIndex && !task.completed)
    .map(({ task, taskIndex }) => ({
      id: `roadmap-${currentItem.id}-${taskIndex}`,
      title: task.title || 'Roadmap task',
      description: task.description || '',
      completed: false,
      dueDate: null,
      estimatedHours: task.estimatedHours,
      estimatedMinutes: task.estimatedMinutes,
      isRoadmapTask: true,
      roadmapTemplateId: currentItem.id,
      roadmapTemplateTitle: currentItem.title,
      roadmapTaskIndex: taskIndex,
    }));
}
